import {
  Box,
  Paper,
  Skeleton,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { ArcElement, Chart as ChartJS, Legend, Tooltip } from "chart.js";
import { Doughnut } from "react-chartjs-2";
import { ChemicalFacilitiesSummaryQuery } from "../../../../../generated-graphql/graphql";
import { blue, green, purple, red, yellow } from "@mui/material/colors";

ChartJS.register(ArcElement, Tooltip, Legend);

type Props = {
  data?: ChemicalFacilitiesSummaryQuery["reportingSeasonSummary"];
  loading: boolean;
};

export function ChemicalFacilitiesDoughnutChart({ data, loading }: Props) {
  const theme = useTheme();

  const chartData = data;

  // Calculate total facilities
  const totalFacilities =
    (chartData?.reportsInNotReporting || 0) +
    (chartData?.noReportsTotal || 0) +
    (chartData?.reportsInAwaitingVerification || 0) +
    (chartData?.reportsInProcessing || 0) +
    (chartData?.reportsInFilingComplete || 0);

  const doughnutChartData = {
    labels: [
      "Not Reporting",
      "No Report",
      "Awaiting Verification",
      "Processing",
      "Filing Complete",
    ],
    datasets: [
      {
        data: [
          chartData?.reportsInNotReporting,
          chartData?.noReportsTotal,
          chartData?.reportsInAwaitingVerification,
          chartData?.reportsInProcessing,
          chartData?.reportsInFilingComplete,
        ],
        backgroundColor: [
          red[300],
          purple[200],
          yellow[400],
          blue[300],
          green[200], // green (filing complete)
        ],
        borderWidth: 0,
      },
    ],
  };

  return (
    <Paper
      elevation={0}
      sx={{
        padding: 3,
        height: "100%",
      }}
    >
      {loading ? (
        <Box 
          sx={{ 
            height: "100%",
            animation: "fadeIn 0.3s ease-in-out",
            "@keyframes fadeIn": {
              from: { opacity: 0 },
              to: { opacity: 1 },
            },
          }}
        >
          <Typography
            variant="body1"
            sx={{
              mb: 3,
              color: theme.palette.text.secondary,
            }}
          >
            Facilities by Report Status
          </Typography>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              height: "calc(100% - 60px)",
              gap: 4,
            }}
          >
            <Box
              sx={{
                position: "relative",
                width: 250,
                height: 250,
                flexShrink: 0,
              }}
            >
              <Skeleton 
                variant="circular" 
                width={250} 
                height={250}
                sx={{
                  animation: "pulse 1.5s ease-in-out infinite",
                  "@keyframes pulse": {
                    "0%": { opacity: 1 },
                    "50%": { opacity: 0.4 },
                    "100%": { opacity: 1 },
                  },
                }}
              />
            </Box>

            <Stack spacing={1} sx={{ width: "100%" }}>
              {Array.from({ length: 5 }).map((_, index) => (
                <Box
                  key={index}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    pb: 1,
                    animation: `slideIn 0.3s ease-out ${index * 0.1}s both`,
                    "@keyframes slideIn": {
                      from: { 
                        opacity: 0,
                        transform: "translateX(-10px)"
                      },
                      to: { 
                        opacity: 1,
                        transform: "translateX(0)"
                      },
                    },
                  }}
                >
                  <Skeleton
                    variant="rectangular"
                    width={16}
                    height={16}
                    sx={{ borderRadius: "2px", flexShrink: 0 }}
                  />
                  <Skeleton variant="text" width="60%" height={20} />
                  <Skeleton variant="text" width="30%" height={20} />
                </Box>
              ))}
            </Stack>
          </Box>
        </Box>
      ) : (
        <Box 
          sx={{ 
            height: "100%",
            animation: "fadeIn 0.5s ease-in-out",
            "@keyframes fadeIn": {
              from: { opacity: 0, transform: "translateY(10px)" },
              to: { opacity: 1, transform: "translateY(0)" },
            },
          }}
        >
          <Typography
            variant="body1"
            sx={{
              mb: 3,
              color: theme.palette.text.secondary,
            }}
          >
            Facilities by Report Status
          </Typography>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              height: "calc(100% - 60px)",
              gap: 4,
            }}
          >
            <Box
              sx={{
                position: "relative",
                width: 250,
                height: 250,
                flexShrink: 0,
              }}
            >
              <Doughnut
                data={doughnutChartData}
                options={{
                  responsive: true,
                  maintainAspectRatio: true,
                  cutout: "60%",
                  color: theme.palette.text.primary,
                  plugins: {
                    legend: {
                      display: false,
                    },
                    tooltip: {
                      callbacks: {
                        label: function (context) {
                          const label = context.label || "";
                          const value = context.parsed || 0;
                          return `${label}: ${value.toLocaleString()} facilities`;
                        },
                      },
                    },
                  },
                }}
              />
              <Box
                sx={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  textAlign: "center",
                }}
              >
                <Typography
                  variant="h5"
                  component="div"
                  sx={{
                    fontWeight: "bold",
                    lineHeight: 1.1,
                    fontSize: "1.75rem",
                  }}
                >
                  {totalFacilities.toLocaleString()}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ lineHeight: 1.1, mt: 0.5, fontSize: "0.875rem" }}
                >
                  Total
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ lineHeight: 1.1, fontSize: "0.875rem" }}
                >
                  Facilities
                </Typography>
              </Box>
            </Box>

            <Stack spacing={1} sx={{ width: "100%" }}>
              {doughnutChartData.labels.map((label, index) => {
                const value = doughnutChartData.datasets[0].data[index] || 0;
                const color =
                  doughnutChartData.datasets[0].backgroundColor[index];
                return (
                  <Box
                    key={label}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      pb: 1,
                    }}
                  >
                    <Box
                      sx={{
                        width: 16,
                        height: 16,
                        backgroundColor: color,
                        borderRadius: "2px",
                        flexShrink: 0,
                      }}
                    />
                    <Typography variant="body2" sx={{ flex: 1 }}>
                      {label}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {value.toLocaleString()} facilities
                    </Typography>
                  </Box>
                );
              })}
            </Stack>
          </Box>
        </Box>
      )}
    </Paper>
  );
}
